import { createApi } from '@reduxjs/toolkit/query/react';
import { config } from '../../config/config';
import {
  GetAllApplicantsRes,
  GetMyAgentsRes,
  IPaginatedCandidatesResponse,
  IUser,
  IUserAgentInfoResponse,
} from '../../interfaces/user.interface';
import { baseQueryWithInterceptor } from '../utils/baseQueryWithInterceptor';
import { UserIncludeEnum } from '../../enums/user-include.enum';

const userApi = createApi({
  reducerPath: 'userApi',
  baseQuery: baseQueryWithInterceptor(`${config.API_BASE_URL}/user`),
  tagTypes: ['user'],

  endpoints: (builder) => ({
    completeOnboarding: builder.mutation<IUser, FormData>({
      query: (data: FormData) => ({
        url: `/onboarding/complete`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['user'],
    }),
    getUser: builder.query<
      IUser,
      { userId: string; includes?: UserIncludeEnum[] }
    >({
      query: ({ userId, includes = [] }) => {
        const includeParam =
          includes.length > 0 ? `?include=${includes.join(',')}` : '';
        return `/${userId}${includeParam}`;
      },
      providesTags: (result, error, { userId }) => [
        { type: 'user' as const, id: userId },
      ],
    }),
    getMe: builder.query<IUser, string[] | void>({
      query: (includes = []) => {
        const includeParam =
          Array.isArray(includes) && includes.length > 0
            ? `?include=${includes.join(',')}`
            : '';
        return `/me${includeParam}`;
      },
      providesTags: ['user'],
    }),
    updateMyProfile: builder.mutation<IUser, FormData>({
      query: (data: FormData) => ({
        url: `/update-profile`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['user'],
    }),
    getUserAgentInfoById: builder.query<IUserAgentInfoResponse, string>({
      query: (agentId) => `/agent-information/${agentId}`,
      providesTags: ['user'],
    }),
    getUserInfo: builder.query<IUser, string>({
      query: (userId) => `/user-basic-info/${userId}`,
      providesTags: ['user'],
    }),
    getAllCandidates: builder.query<
      IPaginatedCandidatesResponse,
      {
        page?: number;
        limit?: number;
        search?: string;
        sort?: string;
        countries?: string[];
        cities?: string[];
        sports?: string;
        athleteActive?: boolean;
        employmentTypes?: string[];
        workTypes?: string[];
        jobTitles?: string[];
      } | void
    >({
      query: (
        args = {
          page: 1,
          limit: 10,
          search: '',
          sort: 'asc',
          countries: [],
          cities: [],
          sports: '',
          athleteActive: undefined,
          employmentTypes: [],
          workTypes: [],
          jobTitles: [],
        }
      ) => {
        const {
          page = 1,
          limit = 10,
          search = '',
          sort = 'asc',
          countries = [],
          cities = [],
          sports = '',
          athleteActive,
          employmentTypes = [],
          workTypes = [],
          jobTitles = [],
        } = args || {};

        const countriesParam = countries.length ? countries.join(',') : '';
        const citiesParam = cities.length ? cities.join(',') : '';
        const employmentTypesParam = employmentTypes.length
          ? employmentTypes.join(',')
          : '';
        const workTypesParam = workTypes.length ? workTypes.join(',') : '';
        const jobTitlesParam = jobTitles.length ? jobTitles.join(',') : '';

        return `candidates?page=${page}&limit=${limit}&search=${search}&sort=${sort}&countries=${countriesParam}&cities=${citiesParam}&sports=${sports}&athleteActive=${athleteActive}&employmentTypes=${employmentTypesParam}&workTypes=${workTypesParam}&jobTitles=${jobTitlesParam}`;
      },
      providesTags: ['user'],
    }),
    updateCompanyAvatar: builder.mutation<IUser, FormData>({
      query: (data: FormData) => ({
        url: 'update-company-avatar',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['user'],
    }),
    updateBanner: builder.mutation<File, FormData>({
      query: (data: FormData) => ({
        url: 'update-banner',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['user'],
    }),
    deleteMyBanner: builder.mutation<IUser, void>({
      query: () => ({
        url: 'delete-banner',
        method: 'DELETE',
      }),
      invalidatesTags: ['user'],
    }),

    // Live Agent
    getAllApplicants: builder.query<
      GetAllApplicantsRes,
      {
        page?: number;
        limit?: number;
        search?: string;
        sort?: string;
        sortType?: string;
      } | void
    >({
      query: (
        args = {
          page: 1,
          limit: 10,
          search: '',
          sort: 'desc',
          sortType: 'country',
        }
      ) => {
        const {
          page = 1,
          limit = 10,
          search = '',
          sort = 'desc',
          sortType = '',
        } = args || {};
        return `/applicants?page=${page}&limit=${limit}&search=${search}&sort=${sort}&sortType=${sortType}`;
      },
      providesTags: ['user'],
    }),

    // Live Agent, part for User
    getMyAgent: builder.query<GetMyAgentsRes, void>({
      query: () => `my-agent`,
      providesTags: ['user'],
    }),

    // Document Management (ID confirmation files)
    uploadDocuments: builder.mutation<any, FormData>({
      query: (data: FormData) => ({
        url: '/documents',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['user'],
    }),

    deleteDocument: builder.mutation<any, string>({
      query: (uniqueKey: string) => ({
        url: `/documents/${uniqueKey}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['user'],
    }),
  }),
});

export const {
  useCompleteOnboardingMutation,
  useGetUserQuery,
  useGetMeQuery,
  useUpdateMyProfileMutation,
  useGetUserInfoQuery,
  useUpdateCompanyAvatarMutation,
  useGetAllCandidatesQuery,
  useGetUserAgentInfoByIdQuery,
  useUpdateBannerMutation,
  useDeleteMyBannerMutation,

  // Live Agent
  useGetAllApplicantsQuery,

  // Live Agent, part for User
  useGetMyAgentQuery,

  // Documents
  useUploadDocumentsMutation,
  useDeleteDocumentMutation,
} = userApi;

export default userApi;
